import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, Printer } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Chip, Empty, Table, Td, Th } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { downloadPdf, printHtml } from "@/lib/printDoc";
import { money, shortDate } from "@/lib/format";

const OK = "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
const WARN = "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300";

const F = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] text-muted-foreground">{label}</Label>{children}
  </div>
);

const Stat = ({ label, value, testId }) => (
  <div className="rounded-xl border border-border bg-muted/25 p-3" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="text-[14px] font-bold text-foreground">{value}</div>
  </div>
);

export const invoiceHtml = (inv) => {
  const cur = inv.currency;
  const org = inv.organisation;
  return `
  <h1>${org.name}</h1>
  ${org.trading_name ? `<div class="muted">${org.trading_name}</div>` : ""}
  ${org.address ? `<div class="muted">${org.address}</div>` : ""}
  ${org.registers?.length ? `<div class="muted">${org.registers.join(" · ")}</div>` : ""}
  ${(org.email || org.phone) ? `<div class="muted">${[org.email, org.phone].filter(Boolean).join(" · ")}</div>` : ""}
  <div class="rule"></div>
  <h2>Hall hire invoice</h2>
  <div class="meta">
    <div><span>Invoice</span>${inv.invoice_ref}</div>
    <div><span>Booking</span>${inv.booking_ref}</div>
    <div><span>Invoice date</span>${inv.invoice_date || "—"}</div>
    <div><span>Payment due</span>${inv.due_on || "—"}</div>
  </div>
  <div class="meta">
    <div><span>Hirer</span>${inv.hirer.name || "—"}</div>
    <div><span>Contact</span>${inv.hirer.contact || "—"}</div>
    <div><span>Space</span>${inv.event.space || "—"}</div>
  </div>
  <table><thead><tr><th>Description</th><th>Detail</th><th class="r">Amount</th></tr></thead>
  <tbody>${inv.lines.map((l) => `<tr><td>${l.description}</td><td class="muted">${l.detail}</td>
    <td class="r">${cur} ${l.amount.toFixed(2)}</td></tr>`).join("")}</tbody>
  <tfoot>
    <tr><td></td><td class="r">Total</td><td class="r">${cur} ${inv.total.toFixed(2)}</td></tr>
    <tr><td></td><td class="r">Deposit held</td><td class="r">${cur} ${inv.deposit_held.toFixed(2)}</td></tr>
    <tr><td></td><td class="r">Received against the hire</td><td class="r">${cur} ${inv.paid.toFixed(2)}</td></tr>
    <tr><td></td><td class="r">Balance due</td><td class="r">${cur} ${inv.balance_due.toFixed(2)}</td></tr>
  </tfoot></table>
  ${inv.payments.length ? `<h2>Payments received</h2><table>
    <thead><tr><th>Date</th><th>Reference</th><th>Type</th><th>Method</th><th class="r">Amount</th></tr></thead>
    <tbody>${inv.payments.map((p) => `<tr><td>${p.received_on}</td>
      <td>${p.reference || p.payment_ref}</td><td>${p.kind}</td>
      <td>${(p.method || "").replace(/_/g, " ")}</td>
      <td class="r">${cur} ${Number(p.amount).toFixed(2)}</td></tr>`).join("")}</tbody></table>` : ""}
  ${inv.footer ? `<p class="muted">${inv.footer}</p>` : ""}`;
};

const AGREEMENT_LABEL = { draft: "raised, not sent", sent: "sent for signature", signed: "signed" };

/** Raise, send and download the one-page hire agreement, and see who signed it. */
const AgreementPanel = ({ booking, canWrite }) => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/property/bookings/${booking.booking_ref}/agreement`);
      setState(res.data);
    } catch (e) {
      if (e?.response?.status === 404) setState({ status: "none" });
      else toast.error(apiError(e));
    }
  }, [booking.booking_ref]);

  useEffect(() => { load(); }, [load]);

  const act = async (key, fn, message) => {
    setBusy(key);
    try {
      const res = await fn();
      toast.success(typeof message === "function" ? message(res) : message);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const link = (path) => `${window.location.origin}${path}`;
  if (!state) return null;

  return (
    <div className="space-y-2 rounded-xl border border-border bg-muted/25 p-3"
         data-testid="agreement-panel">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12px] font-medium text-foreground">Hire agreement</span>
        <Chip testId="agreement-state"
              className={state.status === "signed" ? OK
                : state.status === "sent" ? WARN : "border-border bg-muted text-muted-foreground"}>
          {AGREEMENT_LABEL[state.status] || "not raised"}
        </Chip>
        {state.signed_name && (
          <span className="text-[11px] text-muted-foreground">
            {state.signed_name} · {String(state.signed_at).slice(0, 16).replace("T", " ")}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {canWrite && state.status !== "signed" && (
          <Button size="sm" variant="outline" data-testid="agreement-raise"
                  disabled={busy === "raise"}
                  onClick={() => act("raise",
                    () => api.post(`/property/bookings/${booking.booking_ref}/agreement`, {}),
                    (res) => `${res.data.agreement_ref} raised`)}
                  className="h-7 rounded-full border-border px-2.5 text-[10px]">
            {state.status === "none" ? "Raise the agreement" : "Rebuild from current terms"}
          </Button>
        )}
        {canWrite && ["draft", "sent"].includes(state.status) && (
          <Button size="sm" variant="outline" data-testid="agreement-send" disabled={busy === "send"}
                  onClick={() => act("send",
                    () => api.post(`/property/bookings/${booking.booking_ref}/agreement/send`),
                    (res) => { navigator.clipboard?.writeText(link(res.data.public_path));
                      return "Sent — the signing link is on your clipboard"; })}
                  className="h-7 rounded-full border-border px-2.5 text-[10px]">
            {state.status === "sent" ? "Resend the link" : "Send for signature"}
          </Button>
        )}
        {state.token && (
          <Button size="sm" variant="outline" data-testid="agreement-copy-link"
                  onClick={() => { navigator.clipboard?.writeText(link(state.public_path));
                    toast.success("Signing link copied"); }}
                  className="h-7 rounded-full border-border px-2.5 text-[10px]">Copy link</Button>
        )}
        {state.status !== "none" && (
          <Button size="sm" variant="outline" data-testid="agreement-pdf"
                  onClick={() => downloadPdf(
                    `/property/bookings/${booking.booking_ref}/agreement/pdf`, {},
                    `hire-agreement-${booking.booking_ref}.pdf`).catch((e) => toast.error(apiError(e)))}
                  className="h-7 rounded-full border-border px-2.5 text-[10px]">
            <Download className="mr-1 h-3 w-3" /> PDF
          </Button>
        )}
      </div>
    </div>
  );
};

/** Deposits, payments, the deposit settlement and the invoice for one booking. */
export const BookingFinance = ({ booking, canWrite, canApprove, onOpenChange, onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [payment, setPayment] = useState({ amount: 0, kind: "deposit", method: "bank_transfer",
    reference: "", received_on: "", note: "" });
  const [refund, setRefund] = useState({ amount_withheld: 0, reason: "" });

  const load = useCallback(async () => {
    const res = await api.get(`/property/bookings/${booking.booking_ref}/finance`);
    setData(res.data);
    const depositLeft = res.data.deposit_outstanding;
    setPayment((p) => ({ ...p,
      kind: depositLeft > 0 ? "deposit" : "balance",
      amount: depositLeft > 0 ? depositLeft : Math.max(res.data.hire_balance_due, 0) }));
  }, [booking.booking_ref]);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const act = async (key, fn, message) => {
    setBusy(key);
    try {
      await fn();
      toast.success(message);
      await load();
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const openInvoice = async (mode) => {
    setBusy(`inv-${mode}`);
    try {
      if (mode === "pdf") {
        await downloadPdf(`/property/invoices/${data.invoice_ref}/pdf`, {},
          `hire-invoice-${data.invoice_ref}.pdf`);
      } else {
        const res = await api.get(`/property/invoices/${data.invoice_ref}`);
        printHtml(`Hire invoice ${data.invoice_ref}`, invoiceHtml(res.data));
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const cur = data?.currency || booking.currency || "INR";

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl border-border bg-card" data-testid="booking-finance-dialog">
        <DialogHeader>
          <DialogTitle>{booking.booking_ref} · money on this booking</DialogTitle>
        </DialogHeader>
        {!data ? <div className="py-8 text-center text-[12px] text-muted-foreground">Loading…</div> : (
          <div className="max-h-[72vh] space-y-4 overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat testId="finance-payable" label={data.cancellation_charge > 0 ? "Cancellation charge" : "Hire charge"}
                    value={money(data.hire_charge, cur)} />
              <Stat testId="finance-deposit"
                    label={data.deposit_refundable ? "Deposit held" : "Deposit (part payment)"}
                    value={money(data.deposit_refundable ? data.deposit_held : data.deposit_due, cur)} />
              <Stat testId="finance-paid" label="Received on the hire" value={money(data.hire_paid, cur)} />
              <Stat testId="finance-balance" label="Balance due" value={money(data.balance_due, cur)} />
            </div>
            {data.deposit_outstanding > 0 && (
              <p className="text-[11.5px] text-muted-foreground" data-testid="deposit-outstanding-note">
                {money(data.deposit_outstanding, cur)} deposit still to collect
                {data.deposit_due_on ? ` by ${shortDate(data.deposit_due_on)}` : ""}.
              </p>
            )}

            <div className="flex flex-wrap items-center gap-2 text-[11.5px] text-muted-foreground">
              <Chip testId="finance-invoice-chip" className={data.invoice_status === "invoiced" ? OK : WARN}>
                {(data.invoice_status || "").replace(/_/g, " ")}
              </Chip>
              {data.deposit_due > 0 && data.deposit_due_on
                && <span>deposit due by {shortDate(data.deposit_due_on)}</span>}
              {data.invoice_due_on && <span>· payment due {shortDate(data.invoice_due_on)}</span>}
              {data.invoice_ref && (
                <div className="ml-auto flex gap-2">
                  <Button size="sm" variant="outline" data-testid="finance-print-invoice"
                          disabled={busy === "inv-print"} onClick={() => openInvoice("print")}
                          className="h-7 rounded-full border-border px-2.5 text-[10px]">
                    <Printer className="mr-1 h-3 w-3" /> Print
                  </Button>
                  <Button size="sm" variant="outline" data-testid="finance-pdf-invoice"
                          disabled={busy === "inv-pdf"} onClick={() => openInvoice("pdf")}
                          className="h-7 rounded-full border-border px-2.5 text-[10px]">
                    <Download className="mr-1 h-3 w-3" /> PDF
                  </Button>
                </div>
              )}
            </div>

            {canWrite && data.balance_due > 0.01 && (              <div className="space-y-3 rounded-xl border border-border bg-muted/25 p-3">
                <div className="text-[12px] font-medium text-foreground">Record a payment received</div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <F label="Type">
                    <Select value={payment.kind}
                            onValueChange={(v) => setPayment({ ...payment, kind: v })}>
                      <SelectTrigger data-testid="payment-kind-select" className="bg-muted/40">
                        <SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="deposit">Deposit</SelectItem>
                        <SelectItem value="balance">Balance</SelectItem>
                      </SelectContent>
                    </Select>
                  </F>
                  <F label="Amount">
                    <Input type="number" value={payment.amount} data-testid="payment-amount-input"
                           onChange={(e) => setPayment({ ...payment, amount: Number(e.target.value) })}
                           className="bg-muted/40 font-mono" />
                  </F>
                  <F label="Method">
                    <Select value={payment.method}
                            onValueChange={(v) => setPayment({ ...payment, method: v })}>
                      <SelectTrigger data-testid="payment-method-select" className="bg-muted/40">
                        <SelectValue /></SelectTrigger>
                      <SelectContent>{["bank_transfer", "card", "cash", "cheque", "upi"].map((m) => (
                        <SelectItem key={m} value={m}>{m.replace(/_/g, " ")}</SelectItem>))}</SelectContent>
                    </Select>
                  </F>
                  <F label="Their reference">
                    <Input value={payment.reference} data-testid="payment-reference-input"
                           onChange={(e) => setPayment({ ...payment, reference: e.target.value })}
                           className="bg-muted/40 font-mono text-[11.5px]" />
                  </F>
                </div>
                <Button size="sm" data-testid="payment-save-button"
                        disabled={busy === "pay" || !payment.amount || payment.amount <= 0}
                        onClick={() => act("pay", () => api.post(
                          `/property/bookings/${booking.booking_ref}/payments`,
                          { ...payment, received_on: payment.received_on || null }),
                          "Payment recorded")}
                        className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                  {busy === "pay" && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                  Record {money(payment.amount || 0, cur)}
                </Button>
              </div>
            )}

            {canApprove && data.deposit_held > 0 && !data.deposit_refunded && data.deposit_refundable && (
              <div className="space-y-3 rounded-xl border border-border bg-muted/25 p-3">
                <div className="text-[12px] font-medium text-foreground">
                  Settle the deposit ({money(data.deposit_held, cur)} held)
                </div>
                {data.hire_balance_due > 0.01 ? (
                  <p className="text-[11.5px] text-muted-foreground" data-testid="settle-blocked-note">
                    Collect the {money(data.hire_balance_due, cur)} still outstanding on the hire
                    before the deposit is returned.
                  </p>
                ) : (
                  <>
                <div className="grid gap-3 sm:grid-cols-[160px_1fr]">
                  <F label="Amount kept back">
                    <Input type="number" value={refund.amount_withheld}
                           data-testid="refund-withheld-input"
                           onChange={(e) => setRefund({ ...refund, amount_withheld: Number(e.target.value) })}
                           className="bg-muted/40 font-mono" />
                  </F>
                  <F label="Why (needed only if you keep any of it)">
                    <Textarea rows={2} value={refund.reason} data-testid="refund-reason-input"
                              onChange={(e) => setRefund({ ...refund, reason: e.target.value })}
                              className="bg-muted/40 text-[12px]" />
                  </F>
                </div>
                <Button size="sm" data-testid="refund-deposit-button" disabled={busy === "refund"}
                        onClick={() => act("refund", () => api.post(
                          `/property/bookings/${booking.booking_ref}/deposit/refund`, refund),
                          "Deposit settled")}
                        className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                  {busy === "refund" && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                  Return {money(Math.max(data.deposit_held - (refund.amount_withheld || 0), 0), cur)}
                </Button>
                  </>
                )}
              </div>
            )}

            {data.deposit_refunded && (
              <p className="text-[11.5px] text-muted-foreground" data-testid="deposit-settled-note">
                Deposit settled — {money(data.deposit_withheld, cur)} kept back
                {data.booking.deposit_withheld_reason ? ` (${data.booking.deposit_withheld_reason})` : ""}.
              </p>
            )}

            <AgreementPanel booking={booking} canWrite={canWrite} />

            <Table testId="booking-payments-table"                   head={<><Th>Date</Th><Th>Reference</Th><Th>Type</Th><Th>Method</Th><Th>Amount</Th></>}>
              {data.payments.length === 0 && <Empty colSpan={5} label="Nothing received yet" />}
              {data.payments.map((p) => (
                <tr key={p.payment_ref} data-testid={`payment-row-${p.payment_ref}`}>
                  <Td className="font-mono text-[11px]">{p.received_on}</Td>
                  <Td className="font-mono text-[11px]">{p.reference || p.payment_ref}</Td>
                  <Td className="text-[11px]">{p.kind}</Td>
                  <Td className="text-[11px]">{(p.method || "").replace(/_/g, " ")}</Td>
                  <Td className="font-mono text-[11px]">
                    {p.kind === "refund" ? "−" : ""}{money(p.amount, cur)}</Td>
                </tr>
              ))}
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
